/**
 * ADR-0223 §3.2 — This week (`/app/collab`): the week strip, the ideas in
 * motion, what was decided this week, Atlas changed this week (when the
 * Releases ledger serves it), who is holding what. Everything is served; the
 * page draws it.
 */

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { Board, CollabPage, errMessage, Face, MeetingTile, type Week } from "../components/collab.js";

/** ADR-0224's `GET /tenant/releases/week`: the panel Collab shows is served ready-made. */
type ReleasesWeek = { week?: { panel?: { title: string; lines: string[]; more: number } } } | null;

export function CollabWeek() {
  const [params] = useSearchParams();
  const start = params.get("start");
  const [week, setWeek] = useState<Week | null>(null);
  const [releases, setReleases] = useState<{ lines: string[]; more: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void apiFetch<{ week: Week }>(`/api/tenant/collab/week${start ? `?start=${encodeURIComponent(start)}` : ""}`)
      .then((r) => {
        if (!cancelled) setWeek(r.week);
      })
      .catch((e) => {
        if (!cancelled) setError(errMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // Spec §3.2: the week's shipped changes from the Releases ledger (ADR-0224) — absent until it lands.
    void apiFetch<ReleasesWeek>("/api/tenant/releases/week")
      .then((r) => {
        if (cancelled || !r?.week?.panel) return;
        const lines = r.week.panel.lines.slice(0, 4);
        if (lines.length) setReleases({ lines, more: r.week.panel.more });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [start]);

  return (
    <CollabPage title="This week">
      {error ? <ErrorState detail={error} /> : null}
      {!error && loading ? <LoadingState /> : null}
      {!error && !loading && week ? <WeekView week={week} releases={releases} /> : null}
    </CollabPage>
  );
}

/** Pure, for the tests: the served week drawn as the mock-up draws it. */
export function WeekView(props: { week: Week; releases: { lines: string[]; more: number } | null }) {
  const { week } = props;
  return (
    <>
      <div className="collabHead">
        <div className="collabKicker">{week.kicker}</div>
      </div>
      <section className="collabWeek" aria-label="This week's meetings">
        <div className="collabWeekGrid">
          {week.days.map((d) => (
            <div className={`collabDay${d.today ? " collabDayToday" : ""}`} key={d.date}>
              <div className="collabDayHead">
                <span className="collabDayName">{d.weekday}</span>
                <span className="collabDayNum">{d.day_number}</span>
                {d.today ? <span className="collabTodayTag">Today</span> : null}
              </div>
              {d.meetings.length === 0 ? <span className="collabDayEmpty">{d.empty_line}</span> : d.meetings.map((o) => <MeetingTile key={`${o.kind}:${o.series_id ?? o.session_id}:${o.date}`} o={o} />)}
            </div>
          ))}
        </div>
      </section>
      <div className="collabSplit">
        <section>
          <div className="collabSecHead">
            <h2 className="collabH2">Ideas in motion</h2>
            <Link className="more" to="/app/collab/ideas">
              All ideas →
            </Link>
          </div>
          <Board
            ideas={week.ideas}
            limit={6}
            more={(state, count) => (
              <Link className="collabLinkBtn" to={`/app/collab/ideas?state=${state}&view=list`} style={{ justifySelf: "start" }}>
                All {count} →
              </Link>
            )}
          />
        </section>
        <aside className="collabRail">
          {props.releases ? (
            <div className="collabBox">
              <h3>Atlas changed this week</h3>
              {props.releases.lines.map((l, i) => (
                <div className="collabDecText" key={i} style={{ fontSize: 13 }}>
                  {l}
                </div>
              ))}
              {props.releases.more > 0 ? (
                <Link className="collabLinkBtn" to="/app/whats-new">
                  +{props.releases.more} more
                </Link>
              ) : null}
            </div>
          ) : null}
          <div className="collabBox">
            <h3>Decided this week</h3>
            {week.decided.length === 0 ? <div className="collabEmpty">Nothing decided yet this week.</div> : null}
            {week.decided.map((d, i) => (
              <div className="collabDec" key={`${d.session_id}:${i}`}>
                <span className="collabCheck">✓</span>
                <div>
                  <div className="collabDecText">{d.text}</div>
                  <div className="collabDecWhere">
                    <Link to={d.href}>{d.where}</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="collabBox">
            <h3>Who is holding what</h3>
            {week.holding.length === 0 ? <div className="collabEmpty">Nothing open is held.</div> : null}
            {week.holding.map((h) => (
              <div className="collabHolder" key={h.person?.name ?? "nobody"}>
                <div className="collabHolderHead">
                  {h.person ? <Face face={{ name: h.person.name, initials: initialsOf(h.person.name) }} size={22} /> : <span className="collabFace" style={{ background: "#C3CCD6", width: 22, height: 22, fontSize: 10 }}>?</span>}
                  <span>{h.person?.name ?? "Nobody yet"}</span>
                  <span className="n">{h.count_line}</span>
                </div>
                {h.items.map((it) => (
                  <div className="collabHolderItem" key={`${it.kind}:${it.ref_id}`}>
                    <Link to={it.href}>{it.title}</Link>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </aside>
      </div>
    </>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.length <= 1 ? (parts[0] ?? "?").slice(0, 2).toUpperCase() : `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}
